import type { PrintMenuItem } from "@/pages/human-resources/time-management/components/print-menu/PrintMenuItem";
import usePrintMonthlySchedule from "@/pages/human-resources/time-management/services/hooks/usePrintMonthlySchedule";
import useShowError from "@/shared/custom-hook/useShowError";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWithThreeDotsOverlay } from "@/shared/components/generic/hooks/useWithThreeDotsOverlay.ts";

type Action = "print" | "printDetails";

interface Controller {
  menuItems: PrintMenuItem<Action>[];
  runAction: (action: Action) => void;
}

export default function useMonthlySchedulePrintMenuController(): Controller {
  const withThreeDotsOverlay = useWithThreeDotsOverlay();
  const { t } = useTranslation(["humanResources"], {
    keyPrefix: "timeManagement.print",
  });

  const [printFailed, setPrintFailed] = useState(false);
  useShowError(printFailed, t("error"));
  const printMonthly = usePrintMonthlySchedule();

  const menuItems: PrintMenuItem<Action>[] = [
    {
      action: "print",
      label: t("monthlySchedule.print"),
    },
    {
      action: "printDetails",
      label: t("monthlySchedule.printDetails"),
    },
  ];

  const runAction = useCallback(
    (action: Action): void => {
      setPrintFailed(false);
      const handleSuccess = (success: boolean): void => {
        setPrintFailed(!success);
      };
      switch (action) {
        case "print":
          void withThreeDotsOverlay(async () => {
            handleSuccess(await printMonthly.print());
          });
          break;
        case "printDetails":
          void withThreeDotsOverlay(async () => {
            handleSuccess(await printMonthly.printWithDetails());
          });
          break;
      }
    },
    [printMonthly, withThreeDotsOverlay],
  );
  return { menuItems, runAction };
}
